import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Tabs, Button, Typography, Space, Tooltip, Badge, Drawer, List, Tag, Dropdown, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  BookOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  TeamOutlined,
  CloudServerOutlined,
  CloudOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  SafetyOutlined,
  CodeOutlined,
  BellOutlined,
  WarningOutlined,
  CarOutlined,
  KeyOutlined,
  QrcodeOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { brand, gray, font, layout } from '../theme/tokens';
import { activityLogAPI, dashboardAPI, authMeAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import DashboardPage from '../pages/DashboardPage';
import ProtocolListPage from '../pages/ProtocolListPage';
import ProtocolFormPage from '../pages/ProtocolFormPage';
import SchedulePage from '../pages/SchedulePage';
import MasterPage from '../pages/MasterPage';
import SystemGuidePage from '../pages/SystemGuidePage';
import TapoManagementPage from "../pages/TapoManagementPage";
import ActivityLogPage from '../pages/ActivityLogPage';
import PartnerAccountPage from '../pages/PartnerAccountPage';
import MonitoringPage from '../pages/MonitoringPage';
import RegistryPage from '../pages/RegistryPage';
import StoragePage from '../pages/StoragePage';
import ServerLogPage from '../pages/ServerLogPage';
import UserManagementPage from '../pages/UserManagementPage';
import RoleManagementPage from '../pages/RoleManagementPage';
import TerminalPage from '../pages/TerminalPage';
import ChatBot from './ChatBot';
import GlobalSearch from './GlobalSearch';
import BusinessTripPage from '../pages/BusinessTripPage';
import QRManagementPage from '../pages/QRManagementPage';
import BoardPage from '../pages/BoardPage';
import AdbDemoPage from '../pages/AdbDemoPage';
import { getUsername, clearAuth } from '../services/authToken';

const { Header, Sider, Content } = Layout;

interface TabItem {
  key: string;
  label: string;
  closable: boolean;
  icon?: React.ReactNode;
}

type MenuItem = Required<MenuProps>['items'][number];

// 메뉴코드 → 아이콘 매핑 (정적)
const ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  schedules: <CalendarOutlined />,
  protocols: <FileTextOutlined />,
  master: <DatabaseOutlined />,
  monitoring: <CloudServerOutlined />,
  registry: <InboxOutlined />,
  storage_management: <CloudOutlined />,
  server_logs: <FileTextOutlined />,
  activity_logs: <HistoryOutlined />,
  partner_accounts: <TeamOutlined />,
  user_management: <UserOutlined />,
  role_management: <SafetyOutlined />,
  terminal: <CodeOutlined />,
  guide: <BookOutlined />,
  business_trips: <CarOutlined />,
  qr_management: <QrcodeOutlined />,
  board: <NotificationOutlined />,
  adb_demo: <DatabaseOutlined />,
};

// 시스템 콘솔(anubis-ankt)에 노출할 메뉴 화이트리스트 — 민감/인프라 기능만
const CONSOLE_MENU_CODES = new Set<string>([
  'terminal', 'monitoring', 'registry', 'server_logs',
  'storage_management', 'activity_logs', 'user_management', 'role_management',
]);

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const username = getUsername() || 'admin';
  const { permissions, hasPermission, loaded: permLoaded, reload: reloadPermissions } = usePermission();

  // 권한 기반 동적 메뉴 생성 (DB sort_order/group_sort_order 순) — 콘솔 화이트리스트로 제한
  const { menuItems, flatMenuMap } = useMemo(() => {
    const items: MenuItem[] = [];
    const fMap: Record<string, { label: string; icon: React.ReactNode }> = {};

    // permissions는 이미 group_sort_order → sort_order 순 정렬됨 (콘솔 메뉴만 필터)
    const consolePerms = permissions.filter(p => CONSOLE_MENU_CODES.has(p.menu_code));
    const groupOrder: string[] = [];
    const grouped: Record<string, typeof permissions> = {};
    for (const p of consolePerms) {
      const group = p.menu_group || '';
      if (!grouped[group]) { grouped[group] = []; groupOrder.push(group); }
      grouped[group].push(p);
    }

    let first = true;
    for (const groupName of groupOrder) {
      const perms = grouped[groupName];
      if (!perms || perms.length === 0) continue;
      if (!first) items.push({ type: 'divider' } as MenuItem);
      first = false;

      const children = perms.map(p => {
        const icon = ICON_MAP[p.menu_code] || <FileTextOutlined />;
        fMap[p.menu_path] = { label: p.menu_name, icon };
        return { key: p.menu_path, icon, label: p.menu_name };
      });

      if (groupName && groupName !== '대시보드') {
        items.push({
          key: `grp-${groupName}`,
          type: 'group',
          label: groupName,
          children,
        } as MenuItem);
      } else {
        children.forEach(c => items.push(c as MenuItem));
      }
    }

    return { menuItems: items, flatMenuMap: fMap };
  }, [permissions]);

  const [tabs, setTabs] = useState<TabItem[]>([
    { key: '/', label: '대시보드', closable: false, icon: <DashboardOutlined /> },
  ]);
  const [activeKey, setActiveKey] = useState('/');
  const [collapsed, setCollapsed] = useState(false);
  const [mountedKeys, setMountedKeys] = useState<string[]>(['/']);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwForm] = Form.useForm();

  useEffect(() => {
    dashboardAPI.alerts().then(res => setAlerts(res.data)).catch(() => {});
  }, []);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        Modal.error({ title: '비밀번호 불일치', content: '새 비밀번호가 일치하지 않습니다.' });
        return;
      }
      await authMeAPI.changePassword({ current_password: values.current_password, new_password: values.new_password });
      Modal.success({ title: '변경 완료', content: '비밀번호가 변경되었습니다.' });
      setPwModalOpen(false);
      pwForm.resetFields();
    } catch (err: any) {
      Modal.error({ title: '변경 실패', content: err?.response?.data?.detail || '오류가 발생했습니다.' });
    }
  };

  const openTab = useCallback((key: string, label?: string) => {
    setTabs((prev) => {
      if (prev.find((t) => t.key === key)) return prev;
      const def = flatMenuMap[key];
      return [...prev, {
        key,
        label: label || def?.label || key,
        closable: key !== '/',
        icon: def?.icon,
      }];
    });
    setMountedKeys((prev) => prev.includes(key) ? prev : [...prev, key]);
    setActiveKey(key);
  }, [flatMenuMap]);

  const handleMenuClick = useCallback(({ key }: { key: string }) => {
    openTab(key);
    navigate(key);
    const def = flatMenuMap[key];
    if (def) activityLogAPI.logPageView(key, def.label);
  }, [openTab, navigate, flatMenuMap]);

  const handleTabChange = useCallback((key: string) => {
    setActiveKey(key);
    navigate(key);
  }, [navigate]);

  const handleTabEdit = useCallback((targetKey: any, action: 'add' | 'remove') => {
    if (action !== 'remove') return;
    const key = targetKey as string;
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.key === key);
      const newTabs = prev.filter((t) => t.key !== key);
      if (key === activeKey && newTabs.length > 0) {
        const newActive = newTabs[Math.min(idx, newTabs.length - 1)].key;
        setActiveKey(newActive);
        navigate(newActive);
      }
      return newTabs;
    });
    setMountedKeys((prev) => prev.filter((k) => k !== key));
  }, [activeKey, navigate]);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/login') return;
    if (path.startsWith('/protocols/') && path.endsWith('/edit')) {
      openTab(path, `계획서 편집 #${path.split('/')[2]}`);
    } else {
      openTab(path);
    }
  }, [location.pathname, openTab]);

  // 권한 로드 완료 후 raw path로 등록된 탭 label 갱신 (새로고침 시 flatMenuMap이 늦게 채워지는 경우 대응)
  useEffect(() => {
    if (!permLoaded) return;
    setTabs(prev => prev.map(t => {
      const def = flatMenuMap[t.key];
      if (def && t.label === t.key) {
        return { ...t, label: def.label, icon: def.icon };
      }
      return t;
    }));
  }, [permLoaded, flatMenuMap]);

  const selectedMenuKeys = (() => {
    if (activeKey.startsWith('/protocols/') && activeKey.endsWith('/edit')) return ['/protocols'];
    return [activeKey];
  })();

  const editTabKeys = mountedKeys.filter((k) => k.startsWith('/protocols/') && k.endsWith('/edit'));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* ─── Sidebar ─── */}
      <Sider
        collapsible collapsed={collapsed} onCollapse={setCollapsed}
        breakpoint="lg" theme="dark" width={230} collapsedWidth={60}
        style={{
          background: 'linear-gradient(180deg, #1e1b4b 0%, #272364 50%, #312e81 100%)',
          borderRight: 'none',
          boxShadow: '2px 0 24px rgba(30,27,75,0.15)',
        }}
        trigger={null}
      >
        {/* Logo */}
        <div
          style={{
            height: 64, display: 'flex', alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 18px',
            background: 'rgba(0,0,0,0.22)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <img
            src="/logo-icon.png"
            alt="SAPPT"
            style={{
              width: 38, height: 38, flexShrink: 0,
              marginRight: collapsed ? 0 : 12,
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
            }}
          />
          {!collapsed && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#ffffff', lineHeight: 1.2, letterSpacing: -0.5 }}>
                SAPPT CONSOLE
              </div>
              <div style={{ fontSize: 9, color: 'rgba(248,113,113,0.85)', letterSpacing: 1, textTransform: 'uppercase' as const }}>
                System Admin · MFA
              </div>
            </div>
          )}
        </div>

        {/* Menu */}
        <div style={{ padding: '8px 0', flex: 1, overflowY: 'auto' }}>
          <Menu
            mode="inline"
            selectedKeys={selectedMenuKeys}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: 'none',
              fontSize: 13,
            }}
          />
        </div>

        {/* Sidebar footer */}
        {!collapsed && (
          <div style={{
            padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 10, color: 'rgba(165,180,252,0.35)', textAlign: 'center',
          }}>
            SAPPT Console v1.0 &middot; SECURED
          </div>
        )}
      </Sider>

      <Layout>
        {/* ─── Header ─── */}
        <Header style={{
          background: '#fff', padding: '0 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid #eef0f4', height: layout.headerHeight, lineHeight: `${layout.headerHeight}px`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}>
          {/* Left: Breadcrumb-like */}
          <Space size={8}>
            <Typography.Text style={{ color: gray[400], fontSize: font.size.sm }}>
              시스템 관리 콘솔
            </Typography.Text>
            <span style={{ color: gray[200] }}>|</span>
            <Typography.Text strong style={{ color: gray[600], fontSize: font.size.sm }}>
              {flatMenuMap[activeKey]?.label || ''}
            </Typography.Text>
            <span style={{ color: '#e2e8f0', margin: '0 4px' }}>|</span>
            <GlobalSearch onNavigate={(path, label) => { openTab(path, label); navigate(path); }} />
          </Space>

          {/* Right: Actions */}
          <Space size={8}>
            <Tooltip title="시스템 가이드">
              <Button
                type="text" size="small"
                icon={<QuestionCircleOutlined style={{ color: gray[400] }} />}
                onClick={() => { openTab('/guide'); navigate('/guide'); }}
              />
            </Tooltip>
            <Tooltip title="서버 모니터링">
              <Button
                type="text" size="small"
                icon={<CloudServerOutlined style={{ color: gray[400] }} />}
                onClick={() => { openTab('/monitoring'); navigate('/monitoring'); }}
              />
            </Tooltip>

            {/* 알림 벨 */}
            <Tooltip title={alerts.length > 0 ? `미처리 알림 ${alerts.length}건` : '알림 없음'}>
              <Badge count={alerts.length} size="small" offset={[-2, 2]}>
                <Button
                  type="text" size="small"
                  icon={<BellOutlined style={{ color: alerts.length > 0 ? '#ef4444' : gray[400], fontSize: 15 }} />}
                  onClick={() => setAlertDrawerOpen(true)}
                />
              </Badge>
            </Tooltip>

            <div style={{ width: 1, height: 20, background: '#eef0f4', margin: '0 4px' }} />

            {/* User avatar dropdown */}
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'change-password',
                    icon: <KeyOutlined />,
                    label: '비밀번호 변경',
                    onClick: () => { pwForm.resetFields(); setPwModalOpen(true); },
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: '로그아웃',
                    onClick: handleLogout,
                  },
                ],
              }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '5px 14px 5px 5px', borderRadius: 24,
                  background: '#f8fafc', border: '1px solid #eef0f4',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4338ca, #6366f1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(99,102,241,0.3)',
                }}>
                  <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
                    {(username || 'A')[0].toUpperCase()}
                  </span>
                </div>
                <Typography.Text style={{ color: gray[700], fontSize: 13, fontWeight: 600 }}>
                  {username}
                </Typography.Text>
              </div>
            </Dropdown>

          </Space>
        </Header>

        {/* ─── Tab Bar ─── */}
        <div style={{
          background: '#fff', borderBottom: '1px solid #eef0f4',
          paddingLeft: 12, paddingRight: 12,
        }}>
          <Tabs
            type="editable-card" hideAdd
            activeKey={activeKey} onChange={handleTabChange} onEdit={handleTabEdit}
            items={tabs.map((t) => ({
              key: t.key,
              label: (
                <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {t.icon && <span style={{ fontSize: 12, opacity: 0.7 }}>{t.icon}</span>}
                  {t.label}
                </span>
              ),
              closable: t.closable,
            }))}
            size="small"
            tabBarStyle={{ marginBottom: 0 }}
            tabBarGutter={4}
          />
        </div>

        {/* ─── Content ─── */}
        <Content style={{ padding: 20, background: '#f4f6f9', minHeight: 'calc(100vh - 160px)' }}>
          <div style={{ display: activeKey === '/' ? 'block' : 'none' }}><DashboardPage /></div>
          {mountedKeys.includes('/schedules') && <div style={{ display: activeKey === '/schedules' ? 'block' : 'none' }}><SchedulePage /></div>}
          {mountedKeys.includes('/master') && <div style={{ display: activeKey === '/master' ? 'block' : 'none' }}><MasterPage /></div>}
          {mountedKeys.includes('/protocols') && <div style={{ display: activeKey === '/protocols' ? 'block' : 'none' }}><ProtocolListPage /></div>}
          {editTabKeys.map((tabKey) => <div key={tabKey} style={{ display: activeKey === tabKey ? 'block' : 'none' }}><ProtocolFormPage editId={tabKey.split('/')[2]} /></div>)}
          {mountedKeys.includes('/partner-accounts') && <div style={{ display: activeKey === '/partner-accounts' ? 'block' : 'none' }}><PartnerAccountPage /></div>}
          {mountedKeys.includes('/monitoring') && <div style={{ display: activeKey === '/monitoring' ? 'block' : 'none' }}><MonitoringPage /></div>}
          {mountedKeys.includes('/registry') && <div style={{ display: activeKey === '/registry' ? 'block' : 'none' }}><RegistryPage /></div>}
          {mountedKeys.includes('/storage-management') && <div style={{ display: activeKey === '/storage-management' ? 'block' : 'none' }}><StoragePage /></div>}
          {mountedKeys.includes('/server-logs') && <div style={{ display: activeKey === '/server-logs' ? 'block' : 'none' }}><ServerLogPage /></div>}
          {mountedKeys.includes('/activity-logs') && <div style={{ display: activeKey === '/activity-logs' ? 'block' : 'none' }}><ActivityLogPage /></div>}
          {mountedKeys.includes('/users') && <div style={{ display: activeKey === '/users' ? 'block' : 'none' }}><UserManagementPage /></div>}
          {mountedKeys.includes('/roles') && <div style={{ display: activeKey === '/roles' ? 'block' : 'none' }}><RoleManagementPage /></div>}
          {mountedKeys.includes('/terminal') && <div style={{ display: activeKey === '/terminal' ? 'block' : 'none' }}><TerminalPage /></div>}
          {mountedKeys.includes('/guide') && <div style={{ display: activeKey === '/guide' ? 'block' : 'none' }}><SystemGuidePage /></div>}
          {mountedKeys.includes('/adb-demo') && <div style={{ display: activeKey === '/adb-demo' ? 'block' : 'none' }}><AdbDemoPage /></div>}
          {mountedKeys.includes('/business-trips') && <div style={{ display: activeKey === '/business-trips' ? 'block' : 'none' }}><BusinessTripPage /></div>}
          {mountedKeys.includes('/board') && <div style={{ display: activeKey === '/board' ? 'block' : 'none' }}><BoardPage /></div>}
          {mountedKeys.includes('/qr-management') && <div style={{ display: activeKey === '/qr-management' ? 'block' : 'none' }}><QRManagementPage /></div>}
          {mountedKeys.includes("/tapo") && <div style={{ display: activeKey === "/tapo" ? "block" : "none" }}><TapoManagementPage /></div>}
        </Content>
        <ChatBot />
      </Layout>

      {/* ─── 알림 Drawer ─── */}
      <Drawer
        title={
          <Space>
            <WarningOutlined style={{ color: '#ef4444' }} />
            <span style={{ fontWeight: 700 }}>정식시기 미등록 알림</span>
            <Tag color="red">{alerts.length}건</Tag>
          </Space>
        }
        open={alertDrawerOpen}
        onClose={() => setAlertDrawerOpen(false)}
        width={480}
        placement="right"
      >
        {alerts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: gray[400] }}>
            미처리 알림이 없습니다.
          </div>
        ) : (
          <List
            dataSource={alerts}
            renderItem={(item: any) => (
              <List.Item style={{ padding: '12px 0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Space size={6}>
                      {item.company_name && <Tag color="blue" style={{ fontWeight: 600 }}>{item.company_name}</Tag>}
                      <span style={{ fontWeight: 700, color: brand.primary, fontSize: 13 }}>{item.test_number}</span>
                    </Space>
                    <Tag color="red" style={{ fontSize: 11 }}>+{item.overdue_days}일 경과</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 4, fontWeight: 500 }}>{item.title}</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: gray[400] }}>
                    <span>개시일: <b style={{ color: '#ef4444' }}>{item.start_date}</b></span>
                    <span>상태: <b style={{ color: '#374151' }}>{item.status}</b></span>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Modal
        title={<><KeyOutlined style={{ marginRight: 8, color: '#6366f1' }} />비밀번호 변경</>}
        open={pwModalOpen}
        onCancel={() => setPwModalOpen(false)}
        onOk={handleChangePassword}
        okText="변경"
        width={420}
        maskClosable={false}
      >
        <Form form={pwForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="current_password" label="현재 비밀번호"
            rules={[{ required: true, message: '현재 비밀번호를 입력하세요' }]}>
            <Input.Password placeholder="현재 비밀번호" />
          </Form.Item>
          <Form.Item name="new_password" label="새 비밀번호"
            rules={[{ required: true, message: '새 비밀번호를 입력하세요' }, { min: 4, message: '4자 이상 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 (4자 이상)" />
          </Form.Item>
          <Form.Item name="confirm_password" label="새 비밀번호 확인"
            rules={[{ required: true, message: '비밀번호를 다시 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 확인" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MainLayout;
